// =============================================================================
// UPLOAD PORTAL - MAIN WRAPPER (UploadPortal.js)
// =============================================================================
// This is the entry point for the CDiC Upload Portal tab in the visualizer.
// It replaces React Router with a simple state-based view switcher.
//
// HOW IT WORKS:
// - currentView state controls which page is shown ('login', 'upload', etc.)
// - Each page component receives an onNavigate prop to switch views
// - AuthProvider wraps everything so all pages can access login state
// - The outer <div className="cdic-portal"> scopes all CSS
//
// VIEWS:
//   'login'           → Login form
//   'upload'          → File upload page (after login)
//   'new-password'    → Set permanent password (after temp password login)
//   'forgot-password' → Request a reset code
//   'reset-password'  → Enter code + new password
// =============================================================================

import React, { useState, useCallback } from 'react';
import { AuthProvider, useAuth } from './PortalAuthContext';
import PortalLoginPage from './PortalLoginPage';
import PortalUploadPage from './PortalUploadPage';
import PortalNewPasswordPage from './PortalNewPasswordPage';
import PortalForgotPasswordPage from './PortalForgotPasswordPage';
import PortalResetPasswordPage from './PortalResetPasswordPage';
import './UploadPortal.css';

// Inner component that uses auth context
function UploadPortalInner() {
  const [currentView, setCurrentView] = useState('login');
  const [resetEmail, setResetEmail] = useState('');
  const { isAuthenticated, loading } = useAuth();

  // Navigation function passed to all child components
  const handleNavigate = useCallback((view, data) => {
    if (data && data.email) {
      setResetEmail(data.email);
    }
    setCurrentView(view);
  }, []);

  // Show loading state while checking session
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
        Checking login status...
      </div>
    );
  }

  // If already authenticated and on login page, go to upload
  const activeView = (isAuthenticated && currentView === 'login') ? 'upload' : currentView;

  // Render the correct view
  const renderView = () => {
    switch (activeView) {
      case 'upload':
        return <PortalUploadPage onNavigate={handleNavigate} />;
      case 'new-password':
        return <PortalNewPasswordPage onNavigate={handleNavigate} />;
      case 'forgot-password':
        return <PortalForgotPasswordPage onNavigate={handleNavigate} />;
      case 'reset-password':
        return <PortalResetPasswordPage onNavigate={handleNavigate} initialEmail={resetEmail} />;
      case 'login':
      default:
        return <PortalLoginPage onNavigate={handleNavigate} />;
    }
  };

  return (
    <div className="cdic-portal">
      {renderView()}
    </div>
  );
}

// Outer component wraps with AuthProvider
function UploadPortal() {
  return (
    <AuthProvider>
      <UploadPortalInner />
    </AuthProvider>
  );
}

export default UploadPortal;
